import * as RxDialog from '@radix-ui/react-dialog';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { AssistantLinkCodeDto, AssistantLinkDto } from '@pl/domain';
import { useEffect, useState } from 'react';
import { api } from '../lib/api';
import { qk, useAssistantLinks, useMe } from '../lib/queries';
import { ErrorNote, RefetchNote, Spinner } from '../ui/bits';
import { Modal } from '../ui/Dialog';
import { Icon } from '../ui/Icon';
import s from './LinkWhatsApp.module.css';

/** "9:41": minutes and seconds left, never below 0:00. */
export function formatCountdown(ms: number): string {
  const total = Math.max(0, Math.ceil(ms / 1000));
  return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, '0')}`;
}

/** "123 456": three and three, the way a code is read aloud. */
export const spacedCode = (code: string): string => `${code.slice(0, 3)} ${code.slice(3)}`;

function useNow(active: boolean): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!active) return;
    setNow(Date.now());
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, [active]);
  return now;
}

const shortDay = (iso: string) => new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });

/**
 * Link WhatsApp (spec §9.2): the person gets a six-digit code and sends it to the company's assistant
 * from their phone; that phone then asks as them. The same page sits in the Office shell and in the scan
 * app, so every signed-in person can reach it, and it depends on nothing in Settings.
 *
 * The code lives in this component's state and nowhere else (ruling 1): not in the address, not in
 * storage, not in the query cache, not in a log. Leaving the page or reloading it loses it, and the
 * person gets a new one.
 */
export function LinkWhatsApp({ surface }: { surface: 'office' | 'scan' }) {
  const me = useMe();
  const qc = useQueryClient();
  const [code, setCode] = useState<AssistantLinkCodeDto | null>(null);
  const [confirming, setConfirming] = useState<AssistantLinkDto | null>(null);
  /** A phone linked with the code on screen. The code is spent: it never comes back, even if that number is unlinked. */
  const [linked, setLinked] = useState(false);
  const now = useNow(code !== null);
  const live = code !== null && Date.parse(code.expiresAt) > now;
  const links = useAssistantLinks(live ? 5000 : false);
  // This code's own link: made at or after the server issued it (Task 10 review, I1). Diffing the list
  // before and after took an existing link for it when the first list had not loaded yet.
  const newLink = code !== null && (links.data ?? []).some((l) => Date.parse(l.linkedAt) >= Date.parse(code.issuedAt));
  const linkedNow = linked || newLink;
  useEffect(() => {
    if (!newLink) return;
    setLinked(true);
    setCode(null);
  }, [newLink]);

  const getCode = useMutation({
    mutationFn: () => api.post<AssistantLinkCodeDto>('/assistant/link-codes'),
    onSuccess: (c) => {
      setLinked(false);
      setCode(c);
    },
  });
  const unlink = useMutation({
    mutationFn: (id: string) => api.delete<void>(`/assistant/links/${id}`),
    onSuccess: async () => {
      setConfirming(null);
      // "Linked" was news about a number that may be this one; the list below says what is linked now.
      setLinked(false);
      await qc.invalidateQueries({ queryKey: qk.assistantLinks });
    },
  });
  const closeConfirm = () => {
    setConfirming(null);
    unlink.reset();
  };

  const org = me.data?.org.name ?? 'your company';
  const sendTo = code?.botPhone ? `Send this code to +${code.botPhone} on WhatsApp:` : `Send this code to the ${org} assistant on WhatsApp:`;
  const confirmText = confirming && (
    <>
      <span aria-hidden="true">{confirming.masked}</span>
      <span className={s.srOnly}>{confirming.label}</span> will stop getting answers. You can link it again at any time.
    </>
  );
  // Once a phone has linked, another code is for another phone: offered, but not pressed on the person.
  const again = linkedNow ? 'Link another phone' : code ? 'Get a new code' : 'Get a code';
  const againClass = surface === 'scan' ? (linkedNow ? 'bigbtn ghost' : 'bigbtn') : `${linkedNow ? 'btn' : 'btn primary'} ${s.getCode}`;

  return (
    <div className={surface === 'scan' ? `scan-body ${s.page}` : s.page} data-surface={surface}>
      <section className={s.card} aria-labelledby="wa-title">
        <h2 id="wa-title" className={s.title}>
          Ask about stock on WhatsApp
        </h2>
        <p className={s.lead}>
          Link your phone once. Then message the {org} assistant — “how many cat6 blue in california” — and it answers from the same numbers as this app.
        </p>

        {linkedNow ? (
          <p className={s.ok} role="status">
            <Icon name="check" size={18} /> Linked. You can ask from your phone now.
          </p>
        ) : live ? (
          <div className={s.codeBox}>
            <p className={s.codeLabel}>{sendTo}</p>
            <p className={s.code}>{spacedCode(code!.code)}</p>
            <p className={s.timer} role="timer">
              Valid for {formatCountdown(Date.parse(code!.expiresAt) - now)}
            </p>
          </div>
        ) : code ? (
          <p className={s.expired} role="status">
            That code has expired. Get a new one.
          </p>
        ) : null}

        <ErrorNote error={getCode.error} />
        {!live && (
          // Only once the linked numbers are known: a second wall behind `issuedAt` above.
          <button type="button" className={againClass} onClick={() => getCode.mutate()} disabled={getCode.isPending || links.isPending}>
            {again}
          </button>
        )}
      </section>

      <section className={s.card} aria-labelledby="wa-linked">
        <h3 id="wa-linked" className={s.subtitle}>
          Linked numbers
        </h3>
        {links.isPending ? (
          <Spinner label="Loading linked numbers" />
        ) : links.isLoadingError ? (
          <div className={s.failed}>
            <ErrorNote error={links.error} />
            <button type="button" className="btn sm" onClick={() => void links.refetch()}>
              Try again
            </button>
          </div>
        ) : (links.data ?? []).length === 0 ? (
          <p className={s.muted}>No number is linked yet.</p>
        ) : (
          <ul className={s.list}>
            {(links.data ?? []).map((l) => (
              <li key={l.id} className={s.item}>
                <span className={s.itemText}>
                  {/* Seen masked; heard as "Phone ending 4417", since bullets read aloud as noise. */}
                  <b className={s.masked} aria-hidden="true">
                    {l.masked}
                  </b>
                  <span className={s.srOnly}>{l.label}</span>
                  <small>Linked {shortDay(l.linkedAt)}</small>
                </span>
                <button type="button" className={surface === 'scan' ? s.unlinkScan : 'btn sm'} onClick={() => setConfirming(l)} aria-label={`Unlink ${l.label}`}>
                  Unlink
                </button>
              </li>
            ))}
          </ul>
        )}
        <RefetchNote queries={[links]} />
        <p className={s.muted}>You can also send “unlink” to the assistant from your phone.</p>
      </section>

      {surface === 'office' ? (
        <Modal
          open={confirming !== null}
          onOpenChange={(open) => (open ? undefined : closeConfirm())}
          title="Unlink this number?"
          footer={
            <>
              <button type="button" className="btn" onClick={closeConfirm}>
                Keep it
              </button>
              <button type="button" className="btn primary" onClick={() => confirming && unlink.mutate(confirming.id)} disabled={unlink.isPending}>
                Unlink
              </button>
            </>
          }
        >
          <p className={s.confirmText}>{confirmText}</p>
          <ErrorNote error={unlink.error} />
        </Modal>
      ) : (
        <RxDialog.Root open={confirming !== null} onOpenChange={(open) => (open ? undefined : closeConfirm())}>
          <RxDialog.Portal>
            <RxDialog.Overlay className={s.sheetScrim} />
            <RxDialog.Content className={s.sheet} aria-describedby={undefined}>
              <RxDialog.Title className={s.sheetTitle}>Unlink this number?</RxDialog.Title>
              <p className={s.confirmText}>{confirmText}</p>
              <ErrorNote error={unlink.error} />
              <button type="button" className="bigbtn" onClick={() => confirming && unlink.mutate(confirming.id)} disabled={unlink.isPending}>
                Unlink
              </button>
              <button type="button" className="bigbtn ghost" onClick={closeConfirm}>
                Keep it
              </button>
            </RxDialog.Content>
          </RxDialog.Portal>
        </RxDialog.Root>
      )}
    </div>
  );
}
